/**
 * SAQ (Scoliosis Appearance Questionnaire)
 * Cuestionario para medir percepción/autoimagen y apariencia en escoliosis
 * 
 * NOTA LEGAL: El SAQ puede estar sujeto a derechos de autor.
 * Los textos oficiales de las preguntas requieren autorización.
 * Esta implementación define la ESTRUCTURA y permite configurar
 * los textos desde administración.
 * 
 * VERSIONES CONOCIDAS:
 * - SAQ original (Sanders et al.): 14 ítems con figuras/imágenes
 * - SAQ-22: versión extendida con 22 ítems
 * - SAQ modificado: versiones adaptadas culturalmente
 * 
 * ESTRUCTURA CONFIGURABLE:
 * - Número de ítems variable según versión
 * - Subescalas opcionales (ej: General, Curve, Prominencia, etc.)
 * - Soporte para imágenes/figuras por ítem (asset pack)
 * - Escala de respuesta configurable (típicamente 1-5)
 * 
 * PUNTUACIÓN:
 * - Cada ítem: típicamente 1 (mejor) a 5 (peor)
 * - Algunos ítems pueden requerir inversión
 * - Score total: suma o media según configuración
 * - Rango típico: 14-70 (versión 14 ítems) o normalizado a porcentaje
 */

// Configuración de subescalas (ejemplo para SAQ-14)
export const SAQ_SUBSCALES = {
  general_appearance: {
    id: 'general_appearance',
    name: 'Apariencia General',
    nameShort: 'General',
    items: [], // Se configura dinámicamente
    color: '#3B82F6', // blue
    description: 'Percepción de la apariencia general del cuerpo'
  },
  curve_appearance: {
    id: 'curve_appearance',
    name: 'Apariencia de la Curva',
    nameShort: 'Curva',
    items: [],
    color: '#EF4444', // red
    description: 'Percepción de la curvatura de la columna'
  },
  trunk_shift: {
    id: 'trunk_shift',
    name: 'Desviación del Tronco',
    nameShort: 'Tronco',
    items: [],
    color: '#8B5CF6', // purple
    description: 'Percepción del desplazamiento del tronco'
  },
  waist_asymmetry: {
    id: 'waist_asymmetry',
    name: 'Asimetría de Cintura',
    nameShort: 'Cintura',
    items: [],
    color: '#10B981', // green
    description: 'Percepción de la asimetría en la zona de la cintura'
  },
  shoulder_asymmetry: {
    id: 'shoulder_asymmetry',
    name: 'Asimetría de Hombros',
    nameShort: 'Hombros',
    items: [],
    color: '#F59E0B', // amber
    description: 'Percepción de la asimetría de los hombros'
  },
  chest_deformity: {
    id: 'chest_deformity',
    name: 'Deformidad Torácica',
    nameShort: 'Tórax',
    items: [],
    color: '#06B6D4', // cyan
    description: 'Percepción de la prominencia o deformidad del tórax'
  }
};

// Configuración por defecto del SAQ
export const SAQ_CONFIG = {
  // Versión del cuestionario
  version: 'SAQ',
  versionDescription: 'Scoliosis Appearance Questionnaire',
  
  // Número de ítems (configurable por versión)
  totalItems: 14,
  
  // Rango de respuestas
  minValue: 1,
  maxValue: 5,
  
  // Dirección del scoring
  // 'lower_better': 1 = mejor apariencia, 5 = peor apariencia
  // 'higher_better': 1 = peor apariencia, 5 = mejor apariencia
  scoringDirection: 'lower_better',
  
  // Método de cálculo del score total
  // 'sum': suma de ítems (rango 14-70 para 14 ítems)
  // 'mean': media de ítems (rango 1-5)
  // 'percentage': normalizado a 0-100%
  totalScoreMethod: 'sum',
  
  // Si se debe mostrar como porcentaje
  showAsPercentage: false,
  
  // Días para advertir de cuestionario reciente
  recentDaysWarning: 56, // ~8 semanas
  
  // Subescalas habilitadas
  enableSubscales: false,
  subscaleConfig: {},
  
  // Soporte para imágenes/figuras
  enableImages: true,
  imageBasePath: '/assets/questionnaires/saq/', // Path configurable
  
  // Textos configurables (placeholder - NO SON LOS OFICIALES)
  texts: {
    title: 'SAQ (Apariencia Escoliosis)',
    fullTitle: 'Cuestionario de Apariencia en Escoliosis (SAQ)',
    instructions: 'Por favor, observe cada imagen y seleccione la opción que mejor representa cómo percibe su cuerpo o el de su hijo/a.',
    instructionsNoImages: 'Por favor, responda a cada pregunta seleccionando la opción que mejor describe su percepción.',
    
    // Opciones de respuesta por defecto
    defaultOptions: [
      { value: 1, label: 'Mejor apariencia / Más satisfecho/a', color: '#10B981' },
      { value: 2, label: 'Buena apariencia', color: '#84CC16' },
      { value: 3, label: 'Apariencia normal', color: '#EAB308' },
      { value: 4, label: 'Apariencia afectada', color: '#F97316' },
      { value: 5, label: 'Peor apariencia / Menos satisfecho/a', color: '#EF4444' }
    ],
    
    // Los ítems (textos placeholder - reemplazar con oficiales)
    items: {
      item1: {
        id: 1,
        text: '[Ítem 1: Texto oficial pendiente de configuración]',
        hasImage: true,
        imageFile: 'item1.png',
        subscale: 'general_appearance',
        reverseScored: false,
        options: null // Usar defaultOptions
      },
      item2: {
        id: 2,
        text: '[Ítem 2: Texto oficial pendiente de configuración]',
        hasImage: true,
        imageFile: 'item2.png',
        subscale: 'curve_appearance',
        reverseScored: false,
        options: null
      },
      item3: {
        id: 3,
        text: '[Ítem 3: Texto oficial pendiente de configuración]',
        hasImage: true,
        imageFile: 'item3.png',
        subscale: 'curve_appearance',
        reverseScored: false,
        options: null
      },
      item4: {
        id: 4,
        text: '[Ítem 4: Texto oficial pendiente de configuración]',
        hasImage: true,
        imageFile: 'item4.png',
        subscale: 'trunk_shift',
        reverseScored: false,
        options: null
      },
      item5: {
        id: 5,
        text: '[Ítem 5: Texto oficial pendiente de configuración]',
        hasImage: true,
        imageFile: 'item5.png',
        subscale: 'trunk_shift',
        reverseScored: false,
        options: null
      },
      item6: {
        id: 6,
        text: '[Ítem 6: Texto oficial pendiente de configuración]',
        hasImage: true,
        imageFile: 'item6.png',
        subscale: 'waist_asymmetry',
        reverseScored: false,
        options: null
      },
      item7: {
        id: 7,
        text: '[Ítem 7: Texto oficial pendiente de configuración]',
        hasImage: true,
        imageFile: 'item7.png',
        subscale: 'waist_asymmetry',
        reverseScored: false,
        options: null
      },
      item8: {
        id: 8,
        text: '[Ítem 8: Texto oficial pendiente de configuración]',
        hasImage: true,
        imageFile: 'item8.png',
        subscale: 'shoulder_asymmetry',
        reverseScored: false,
        options: null
      },
      item9: {
        id: 9,
        text: '[Ítem 9: Texto oficial pendiente de configuración]',
        hasImage: true,
        imageFile: 'item9.png',
        subscale: 'shoulder_asymmetry',
        reverseScored: false,
        options: null
      },
      item10: {
        id: 10,
        text: '[Ítem 10: Texto oficial pendiente de configuración]',
        hasImage: true,
        imageFile: 'item10.png',
        subscale: 'chest_deformity',
        reverseScored: false,
        options: null
      },
      item11: {
        id: 11,
        text: '[Ítem 11: Texto oficial pendiente de configuración]',
        hasImage: true,
        imageFile: 'item11.png',
        subscale: 'chest_deformity',
        reverseScored: false,
        options: null
      },
      item12: {
        id: 12,
        text: '[Ítem 12: Texto oficial pendiente de configuración]',
        hasImage: true,
        imageFile: 'item12.png',
        subscale: 'general_appearance',
        reverseScored: false,
        options: null
      },
      item13: {
        id: 13,
        text: '[Ítem 13: Texto oficial pendiente de configuración]',
        hasImage: true,
        imageFile: 'item13.png',
        subscale: 'general_appearance',
        reverseScored: false,
        options: null
      },
      item14: {
        id: 14,
        text: '[Ítem 14: Texto oficial pendiente de configuración]',
        hasImage: true,
        imageFile: 'item14.png',
        subscale: 'general_appearance',
        reverseScored: false,
        options: null
      }
    }
  }
};

// Schema para el sistema de cuestionarios
export const SAQ_SCHEMA = {
  id: 'saq',
  name: 'SAQ (Apariencia Escoliosis)',
  fullName: 'Scoliosis Appearance Questionnaire',
  description: 'Cuestionario para evaluar la percepción de la apariencia corporal en pacientes con escoliosis. Incluye evaluación visual con figuras.',
  estimatedTime: '5-10 minutos',
  category: 'escoliosis',
  version: 'SAQ',
  requiresLicense: true,
  licenseNote: 'Los textos e imágenes oficiales requieren autorización.',
  isSAQ: true, // Flag para detección especial en el wizard
  targetPopulation: 'Adolescentes y adultos con escoliosis',
  scoring: {
    type: 'sum_or_mean',
    method: 'configurable',
    totalItems: 14,
    range: { min: 14, max: 70 }, // Para suma con 14 ítems
    interpretation: {
      14: 'Sin afectación percibida',
      28: 'Afectación leve',
      42: 'Afectación moderada',
      56: 'Afectación significativa',
      70: 'Afectación severa'
    }
  },
  sections: [
    {
      id: 'saq_all',
      title: 'Cuestionario SAQ',
      questions: Array.from({ length: 14 }, (_, i) => ({
        id: `item${i + 1}`,
        itemNumber: i + 1,
        type: 'saq_item',
        required: true,
        min: 1,
        max: 5
      }))
    }
  ]
};

/**
 * Calcula las puntuaciones del SAQ
 * @param {Object} answers - Objeto con respuestas { item1: 3, item2: 4, ... }
 * @param {Object} config - Configuración del cuestionario
 * @returns {Object} Resultados calculados
 */
export const calculateSAQScore = (answers, config = SAQ_CONFIG) => {
  const results = {
    subscale_scores: {},
    total_score: null,
    total_score_percentage: null,
    mean_score: null,
    missing_items_count: 0,
    missing_items: [],
    answered_items_count: 0,
    is_complete: false,
    interpretation: null
  };

  const totalItems = config.totalItems || 14;

  // Contar respuestas válidas y faltantes
  for (let i = 1; i <= totalItems; i++) {
    const itemKey = `item${i}`;
    const value = answers[itemKey];
    if (value === undefined || value === null) {
      results.missing_items_count++;
      results.missing_items.push(i);
    } else {
      results.answered_items_count++;
    }
  }

  results.is_complete = results.missing_items_count === 0;

  // Calcular score por subescala si está habilitado
  if (config.enableSubscales && config.subscaleConfig) {
    Object.entries(config.subscaleConfig).forEach(([subscaleKey, subscale]) => {
      const subscaleItems = subscale.items || [];
      const subscaleAnswers = subscaleItems
        .map(itemNum => {
          const value = answers[`item${itemNum}`];
          if (value === undefined || value === null) return null;
          
          // Aplicar inversión si es necesario
          const itemConfig = config.texts.items[`item${itemNum}`];
          if (itemConfig?.reverseScored) {
            return (config.maxValue + config.minValue) - value;
          }
          return value;
        })
        .filter(v => v !== null);

      if (subscaleAnswers.length > 0) {
        const sum = subscaleAnswers.reduce((a, b) => a + b, 0);
        const mean = sum / subscaleAnswers.length;
        results.subscale_scores[subscaleKey] = {
          score: config.totalScoreMethod === 'mean' ? Math.round(mean * 100) / 100 : sum,
          mean: Math.round(mean * 100) / 100,
          sum: sum,
          answered: subscaleAnswers.length,
          total: subscaleItems.length,
          percentage: Math.round((subscaleAnswers.length / subscaleItems.length) * 100)
        };
      } else {
        results.subscale_scores[subscaleKey] = {
          score: null,
          mean: null,
          sum: null,
          answered: 0,
          total: subscaleItems.length,
          percentage: 0
        };
      }
    });
  }

  // Calcular score total
  const validAnswers = [];
  for (let i = 1; i <= totalItems; i++) {
    const value = answers[`item${i}`];
    if (value !== undefined && value !== null) {
      const itemConfig = config.texts.items[`item${i}`];
      if (itemConfig?.reverseScored) {
        validAnswers.push((config.maxValue + config.minValue) - value);
      } else {
        validAnswers.push(value);
      }
    }
  }

  if (validAnswers.length > 0) {
    const sum = validAnswers.reduce((a, b) => a + b, 0);
    const mean = sum / validAnswers.length;
    
    // Calcular según método configurado
    if (config.totalScoreMethod === 'sum') {
      results.total_score = sum;
    } else if (config.totalScoreMethod === 'mean') {
      results.total_score = Math.round(mean * 100) / 100;
    } else if (config.totalScoreMethod === 'percentage') {
      // Normalizar a 0-100
      const minPossible = totalItems * config.minValue;
      const maxPossible = totalItems * config.maxValue;
      results.total_score = Math.round(((sum - minPossible) / (maxPossible - minPossible)) * 100);
    }
    
    results.mean_score = Math.round(mean * 100) / 100;
    
    // Calcular porcentaje (para referencia)
    const minPossible = totalItems * config.minValue;
    const maxPossible = totalItems * config.maxValue;
    results.total_score_percentage = Math.round(((sum - minPossible) / (maxPossible - minPossible)) * 100);
  }

  // Interpretación (basada en suma para 14 ítems)
  if (results.total_score !== null && config.totalScoreMethod === 'sum') {
    const score = results.total_score;
    if (score <= 20) {
      results.interpretation = 'Sin afectación percibida';
    } else if (score <= 30) {
      results.interpretation = 'Afectación leve';
    } else if (score <= 45) {
      results.interpretation = 'Afectación moderada';
    } else if (score <= 55) {
      results.interpretation = 'Afectación significativa';
    } else {
      results.interpretation = 'Afectación severa';
    }
  } else if (results.mean_score !== null) {
    const mean = results.mean_score;
    if (mean <= 1.5) {
      results.interpretation = 'Sin afectación percibida';
    } else if (mean <= 2.5) {
      results.interpretation = 'Afectación leve';
    } else if (mean <= 3.5) {
      results.interpretation = 'Afectación moderada';
    } else if (mean <= 4.0) {
      results.interpretation = 'Afectación significativa';
    } else {
      results.interpretation = 'Afectación severa';
    }
  }

  return results;
};

/**
 * Valida las respuestas del SAQ
 * @param {Object} answers - Respuestas { item1: 3, ... }
 * @param {boolean} allowIncomplete - Permitir guardar incompleto
 * @param {Object} config - Configuración
 * @returns {Object} { isValid, errors, warnings }
 */
export const validateSAQAnswers = (answers, allowIncomplete = false, config = SAQ_CONFIG) => {
  const errors = [];
  const warnings = [];
  const totalItems = config.totalItems || 14;

  for (let i = 1; i <= totalItems; i++) {
    const itemKey = `item${i}`;
    const value = answers[itemKey];

    if (value === undefined || value === null) {
      if (!allowIncomplete) {
        errors.push(`El ítem ${i} es obligatorio`);
      } else {
        warnings.push(`El ítem ${i} no ha sido respondido`);
      }
    } else if (value < config.minValue || value > config.maxValue) {
      errors.push(`El ítem ${i} debe estar entre ${config.minValue} y ${config.maxValue}`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    completeness: Math.round(((totalItems - errors.length - warnings.length) / totalItems) * 100)
  };
};

/**
 * Obtiene el color del score
 */
export const getScoreColor = (score, config = SAQ_CONFIG) => {
  if (config.scoringDirection === 'lower_better') {
    // Score más bajo = mejor (verde)
    if (config.totalScoreMethod === 'sum') {
      if (score <= 20) return '#10B981';
      if (score <= 30) return '#84CC16';
      if (score <= 45) return '#EAB308';
      if (score <= 55) return '#F97316';
      return '#EF4444';
    } else {
      if (score <= 1.5) return '#10B981';
      if (score <= 2.5) return '#84CC16';
      if (score <= 3.5) return '#EAB308';
      if (score <= 4.0) return '#F97316';
      return '#EF4444';
    }
  } else {
    // Score más alto = mejor
    if (score >= 4.5) return '#10B981';
    if (score >= 3.5) return '#84CC16';
    if (score >= 2.5) return '#EAB308';
    if (score >= 1.5) return '#F97316';
    return '#EF4444';
  }
};

export default SAQ_SCHEMA;
